import { useEffect, useRef, useState, useMemo } from 'react';
import {
  Form,
  Row,
  Typography,
  Col,
  Button,
  Space,
  Tooltip,
  Popover,
  List,
  Checkbox,
} from 'antd';
import {
  QuestionCircleTwoTone,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { cloneDeep, groupBy, isEmpty } from 'lodash';

import {
  FullWidthSpace,
  MinimumRequirementPropertiesInputProduct,
  OpenUpdateMinimumRequirementButton,
} from 'common/components';

import { additionalRequirementData } from 'static/Constants';
import messages from 'i18n/messages/product';
import { useGetProductFullView } from './product-full/hooks';
import { useIntl } from 'react-intl';
import {
  lowercaseFirstChar,
  useIsValidatingActiveProduct,
} from 'hooks/useValidateSelfLoadedProduct';
import { useValidateActiveProduct } from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';
import {
  addErrorMessage,
  getErrorsForActiveProduct,
} from 'pages/home/ribbon/components/sections/product/components/make-product-active/utils';
import { useGetProductEnums } from 'hooks/useProductEnums';
import { getProductPropertySchema } from 'utils/minimumRequirementUtils';
import { useGetEntityProperties } from 'hooks/entityHooks';
import { covertNestedArrToSingleArr } from 'pages/minimum-requirements/components/modal/utils';
import { getDefaultPrimaryErrors } from 'hooks/usePreviewRequirementForProduct';

const { Text, Title, Paragraph } = Typography;

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const getAdditionalFieldNames = (entityType) => {
  const additionalRequirements = additionalRequirementData[entityType];
  if (!additionalRequirements) return [];

  return additionalRequirements.map((item) => item?.fieldName?.toLowerCase());
};

export const filterRequiredErrorFields = (
  errorFields,
  additionalFieldNames
) => {
  return errorFields.filter(
    (fieldName) => !additionalFieldNames.includes(fieldName?.toLowerCase())
  );
};

const filterAdditionalErrorFields = (errorFields, additionalFieldNames) => {
  return errorFields.filter((fieldName) =>
    additionalFieldNames.includes(fieldName?.toLowerCase())
  );
};

const findFieldData = (fieldName, properties) => {
  return properties?.find(
    (propertyItem) =>
      propertyItem.fieldFullPath === fieldName ||
      propertyItem?.fieldFullPath?.toLowerCase() === fieldName?.toLowerCase()
  );
};

const getCheckStatus = (fieldName, errorFields, primaryErrors) => {
  const normalizedFieldName = fieldName?.toLowerCase();
  const isError = errorFields.some(
    (ef) => ef?.toLowerCase() === normalizedFieldName
  );

  if (!isError) return 'success';

  const error = primaryErrors.find(
    (err) => err.fieldName?.toLowerCase() === normalizedFieldName
  );

  return error?.isWarning ? 'warning' : 'error';
};

export const mapRequiredProperties = (
  allRequiredFields,
  errorFields,
  properties,
  productEnums,
  additionalFieldNames,
  primaryErrors = []
) => {
  const requiredFields = filterRequiredErrorFields(
    allRequiredFields,
    additionalFieldNames
  );

  return requiredFields
    .map((fieldName) => {
      const fieldData = findFieldData(fieldName, properties);

      return {
        ...fieldData,
        id: fieldName,
        fieldName,
        name: fieldData?.displayName || fieldName,
        checkStatus: getCheckStatus(fieldName, errorFields, primaryErrors),
        errorComponent: MinimumRequirementPropertiesInputProduct,
      };
    })
    .map((propertyItem) => {
      const propertySchema = getProductPropertySchema(
        propertyItem,
        productEnums
      );
      return { ...propertyItem, inputSchema: propertySchema };
    });
};

export const mapAdditionalProperties = (
  errorFields,
  additionalRequirements,
  additionalFieldNames,
  entityRequirement
) => {
  const additionalErrors = filterAdditionalErrorFields(
    errorFields,
    additionalFieldNames
  );

  const additionalActiveRequirements = additionalRequirements?.filter(
    (item) => {
      return entityRequirement[lowercaseFirstChar(item?.fieldName)];
    }
  );

  return additionalActiveRequirements.map((item) => {
    return {
      id: item?.fieldName,
      name: item?.fieldDisplayName,
      checkStatus: additionalErrors?.includes(item?.fieldName?.toLowerCase())
        ? 'error'
        : 'success',
      active: !isEmpty(item),
      value: entityRequirement[lowercaseFirstChar(item?.fieldName)],
      errorComponent: OpenUpdateMinimumRequirementButton,
      hiddenTotal: item?.hiddenTotal || false,
    };
  });
};

const checkHasErrors = (requiredProperties, additionalActiveRequirements) => {
  const hasPropertiesRequiredError = requiredProperties?.some(
    (item) => item.checkStatus === 'error'
  );

  const hasAdditionalPropertiesError = additionalActiveRequirements?.some(
    (item) => item.checkStatus === 'error'
  );

  return hasPropertiesRequiredError || hasAdditionalPropertiesError;
};

export const addPrimaryErrorsToBasicModule = (properties) => {
  const defaultPrimaryErrors = getDefaultPrimaryErrors() ?? [];

  if (!properties || !Array.isArray(properties)) {
    return [];
  }

  const result = cloneDeep(properties);

  if (!result[0]) {
    return result;
  }

  if (!result[0].properties) {
    result[0].properties = [];
  }

  result[0].properties = result[0].properties.concat(defaultPrimaryErrors);

  return result;
};

const getValidatingStatus = (loading, hasError) => {
  if (loading) return 'validating';
  return hasError ? 'error' : 'success';
};

// ============================================
// RENDER COMPONENTS
// ============================================

const StatusIcon = ({ propertyItem }) => {
  const statusIconCollection = {
    success: { icon: CheckOutlined, color: '#389e0d' },
    error: { icon: CloseOutlined, color: '#cf1322' },
  };

  const icon = statusIconCollection[propertyItem.checkStatus];
  const StatusIcon = icon?.icon;
  const statusColor = icon?.color;

  return StatusIcon ? (
    <StatusIcon style={{ color: statusColor, margin: '4px 0px' }} />
  ) : null;
};

const PropertyReqModule = ({ item }) => {
  const moduleName = item[0];
  const propertyList = item[1];

  return (
    <div key={moduleName}>
      {moduleName !== 'undefined' && moduleName !== 'null' ? (
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          {moduleName}
        </Text>
      ) : null}
      {propertyList.map((propertyItem, index) => {
        const { fullPathDisplayName, errorMessage } = propertyItem;
        return (
          <Row
            key={`${moduleName}-${index}`}
            style={{ marginBottom: 4 }}
            gutter={16}
            wrap={false}
          >
            <Col flex={1}>
              <Text
                style={{
                  paddingLeft: 8,
                  lineHeight: 'normal',
                  display: 'inline-block',
                }}
              >
                {errorMessage
                  ? errorMessage
                  : fullPathDisplayName + ` is required.`}
              </Text>
            </Col>
            <Col flex={0}>
              <StatusIcon propertyItem={propertyItem} />
            </Col>
          </Row>
        );
      })}
    </div>
  );
};

const AdditionalReqItem = ({ item }) => {
  return (
    <Row style={{ marginBottom: 2 }} gutter={16} wrap={false}>
      <Col flex={1}>
        <Text disabled={!item.active} style={{ paddingLeft: 8 }}>
          {item.errorMessage ? item.errorMessage : item.name}
        </Text>
      </Col>
      <Col flex={0}>{!item.errorMessage ? item.value : ''}</Col>
      <Col flex={0}>
        <StatusIcon propertyItem={item} />
      </Col>
    </Row>
  );
};

const ValidatingInfoPopover = ({
  productFull,
  groupedProReq,
  stableHasMinReqError,
  additionalActiveRequirements,
}) => {
  if (stableHasMinReqError)
    return (
      <FullWidthSpace
        className='scroller--y'
        style={{
          width: 300,
          maxHeight: 360,
          padding: '0px 8px',
          margin: '0px -8px',
        }}
      >
        <Space>
          <Text strong style={{ marginBottom: '0' }}>
            Private Label
          </Text>
          <Checkbox checked={productFull?.isPrivateLabel} />
        </Space>
        <div>
          <Title level={5}>Properties Requirement</Title>
          <List
            dataSource={Object.entries(groupedProReq)}
            renderItem={(item) => <PropertyReqModule item={item} />}
          />
        </div>
        <div>
          <Title level={5}>Additional Requirement</Title>
          <List
            dataSource={additionalActiveRequirements}
            renderItem={(item) => <AdditionalReqItem item={item} />}
          />
        </div>
      </FullWidthSpace>
    );

  return (
    <div
      style={{
        display: 'flex',
        justifyItems: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <CheckCircleOutlined
        style={{ color: '#52c41a', fontSize: 40, margin: 8 }}
      />
      <Typography.Paragraph strong>
        Product has passed all validation rules and is ready to be activated.
      </Typography.Paragraph>
    </div>
  );
};

const ValidationStatusHelp = ({
  loading,
  colorMessage,
  message,
  shouldShowErrorMessage,
  onClickValidateBtn,
  placement,
  infoPopupVisible,
  onInfoPopupVisibleChange,
  stableHasMinReqError,
  productFull,
  groupedProReq,
  additionalActiveRequirements,
  intl,
}) => {
  return (
    <Row style={{ margin: '4px 0' }} wrap={false}>
      <Col flex={1}>
        {loading ? (
          <Paragraph style={{ margin: '1px 0px' }}>
            {intl.formatMessage(messages.validateLoading)}
          </Paragraph>
        ) : (
          <Text type={colorMessage}>{message}</Text>
        )}
      </Col>
      <Col flex={0}>
        {shouldShowErrorMessage && (
          <Space size={4} align='center'>
            <Tooltip title='Click to validate'>
              <Button
                type='text'
                size='small'
                icon={<ReloadOutlined />}
                onClick={onClickValidateBtn}
              />
            </Tooltip>

            <Popover
              placement={placement}
              trigger='click'
              content={
                <ValidatingInfoPopover
                  productFull={productFull}
                  groupedProReq={groupedProReq}
                  additionalActiveRequirements={additionalActiveRequirements}
                  stableHasMinReqError={stableHasMinReqError}
                />
              }
              visible={infoPopupVisible}
              onVisibleChange={onInfoPopupVisibleChange}
            >
              <QuestionCircleTwoTone
                twoToneColor={stableHasMinReqError ? '#f5222d' : '#52c41a'}
              />
            </Popover>
          </Space>
        )}
      </Col>
    </Row>
  );
};

// ============================================
// MAIN HOOK
// ============================================

export const useValidateStatusForProductFull = (props) => {
  const {
    form,
    entityId,
    isPrivateLabel,
    placement = 'rightBottom',
    getFormPayload,
    validatePrimaryFieldsMutation,
    validateProducts,
    setValidateProducts,
    onUpdateValidateProducts,
  } = props;

  const intl = useIntl();
  const prevHasMinReqErrorRef = useRef(null);

  const [infoPopupVisible, setInfoPopupVisible] = useState(false);
  const [requiredProperties, setRequiredProperties] = useState([]);
  const [additionalRequirements, setAdditionalRequirements] = useState([]);

  const value = Form.useWatch('status', form);
  const isActiveProduct = value?.toLowerCase() === 'active';

  const {
    validateProducts: initialValidateProducts,
    isFetching,
    handleRevalidateActiveProduct,
  } = useValidateActiveProduct({
    productIds: [entityId],
    enabled: Boolean(entityId) && isActiveProduct && !isPrivateLabel,
  });

  useEffect(() => {
    if (initialValidateProducts) {
      setValidateProducts(initialValidateProducts);
    }
  }, [initialValidateProducts, setValidateProducts]);

  const { productEnums } = useGetProductEnums();

  const { entityProperties } =
    useGetEntityProperties({
      entityType: 'product',
    }) || [];

  const entityRequirement = useMemo(() => {
    return validateProducts?.[0]?.activeResult?.additionalRequirements || {};
  }, [validateProducts]);

  const allProperties = useMemo(
    () => addPrimaryErrorsToBasicModule(entityProperties),
    [entityProperties]
  );

  const { properties: mappingProperties } = useMemo(
    () => covertNestedArrToSingleArr(allProperties),
    [allProperties]
  );

  const activeResult = useMemo(
    () => validateProducts?.[0]?.activeResult ?? {},
    [validateProducts]
  );

  const errorFields = useMemo(
    () => getErrorsForActiveProduct(activeResult),
    [activeResult]
  );

  useEffect(() => {
    if (!mappingProperties || !productEnums) return;

    const additionalFieldNames = getAdditionalFieldNames('Product');
    const productAdditionalRequirements = additionalRequirementData['Product'];

    const primaryErrors = activeResult?.errors ?? [];
    const allRequiredFields = activeResult?.activeRequiredFields ?? [];

    const mappedRequiredProperties = mapRequiredProperties(
      allRequiredFields,
      errorFields,
      mappingProperties,
      productEnums,
      additionalFieldNames,
      primaryErrors
    );

    const mappedAdditionalProperties = mapAdditionalProperties(
      errorFields,
      productAdditionalRequirements,
      additionalFieldNames,
      entityRequirement
    );

    const requiredFieldsWithErrorMessage = addErrorMessage(
      mappedRequiredProperties,
      activeResult
    );

    const hasErrorFields =
      mappedAdditionalProperties?.some(
        (item) => item.checkStatus === 'error'
      ) ||
      requiredFieldsWithErrorMessage?.some(
        (item) => item.checkStatus === 'error' || item.checkStatus === 'warning'
      );

    setInfoPopupVisible(hasErrorFields);

    setAdditionalRequirements(mappedAdditionalProperties);
    setRequiredProperties(requiredFieldsWithErrorMessage);
  }, [
    errorFields,
    productEnums,
    mappingProperties,
    entityRequirement,
    activeResult,
  ]);

  const isValidatingActiveProduct = useIsValidatingActiveProduct();

  const additionalActiveRequirements = additionalRequirements?.filter(
    (item) => item?.active
  );

  const loading = isFetching || isValidatingActiveProduct;

  const groupedProReq = groupBy(requiredProperties, 'moduleDisplayName');

  const onClickValidateBtn = async () => {
    if (getFormPayload) {
      const payload = getFormPayload();
      const response = await validatePrimaryFieldsMutation.mutateAsync(payload);
      if (response?.isSuccess) {
        onUpdateValidateProducts && onUpdateValidateProducts(response);
      }
    }
  };

  const { productFull } = useGetProductFullView({
    productId: entityId,
  });

  const onInfoPopupVisibleChange = (visible) => {
    setInfoPopupVisible(visible);
  };

  const hasMinReqError = useMemo(
    () => checkHasErrors(requiredProperties, additionalActiveRequirements),
    [requiredProperties, additionalActiveRequirements]
  );

  const stableHasMinReqError = useMemo(() => {
    if (loading) {
      return prevHasMinReqErrorRef.current ?? hasMinReqError;
    }
    prevHasMinReqErrorRef.current = hasMinReqError;
    return hasMinReqError;
  }, [hasMinReqError, loading]);

  const shouldValidate = entityId && isActiveProduct;
  const messageVisible = shouldValidate;
  const shouldShowErrorMessage = shouldValidate && !loading;

  const validatingStatus = getValidatingStatus(loading, stableHasMinReqError);

  const messageType = stableHasMinReqError ? 'warning' : 'activeProductSuccess';
  const colorMessage = stableHasMinReqError ? 'danger' : 'success';
  const message = intl.formatMessage(
    messages.validateStatusMessage[messageType]
  );

  const statusValidateProps = {
    validateStatus: shouldValidate ? validatingStatus : undefined,
    hasFeedback: messageVisible,
    help: messageVisible ? (
      <ValidationStatusHelp
        loading={loading}
        colorMessage={colorMessage}
        message={message}
        shouldShowErrorMessage={shouldShowErrorMessage}
        onClickValidateBtn={onClickValidateBtn}
        placement={placement}
        infoPopupVisible={infoPopupVisible}
        onInfoPopupVisibleChange={onInfoPopupVisibleChange}
        stableHasMinReqError={stableHasMinReqError}
        productFull={productFull}
        groupedProReq={groupedProReq}
        additionalActiveRequirements={additionalActiveRequirements}
        intl={intl}
      />
    ) : null,
  };

  return {
    statusValidateProps,
    onUpdateValidateProducts,
    validateProducts,
    handleRevalidateActiveProduct,
  };
};
